// TOEFL iBT Mock 9 — Listening (2026 format, multistage adaptive)
//
// Task types:
//   response     – Listen and Choose a Response: hear one line, pick the best reply.
//   conversation – Listen to a Conversation (campus / everyday life).
//   announcement – Listen to an Announcement (campus or public notice).
//   talk         – Listen to an Academic Talk (short lecture).
//
// Topics are unique to this mock. The harder Module 2 uses indirect replies,
// inversion ("Not once did…") and questions on the speaker's attitude.
//
// Each recording plays once. Questions appear after it finishes.
// gender ("m" / "f") picks the synthesized voice for each line.

export const MODULE_SECONDS = { module1: 15 * 60, module2: 12 * 60 };

export const TASK_LABELS = {
  response: "Choose a Response",
  conversation: "Conversation",
  announcement: "Announcement",
  talk: "Academic Talk",
};

const W = (text) => ({ speaker: "Woman", gender: "f", text });
const M = (text) => ({ speaker: "Man", gender: "m", text });

const response = (id, line, options, correct) => ({
  id,
  type: "response",
  lines: [line],
  questions: [{ question: "Choose the best response.", options, correct }],
});

export const listeningModules = {
  module1: [
    response("m1-r1", M("Have you seen the new schedule for the shuttle bus?"), [
      "I saw the bus yesterday.",
      "Yes, it now runs every fifteen minutes.",
      "The schedule is printed on paper.",
      "I have a new bike.",
    ], 1),
    response("m1-r2", W("I'm so nervous about my job interview tomorrow."), [
      "Just be yourself, and you'll do fine.",
      "I interviewed a professor.",
      "Tomorrow is Friday.",
      "The job pays well.",
    ], 0),
    response("m1-r3", M("Could you save me a seat at the lecture?"), [
      "I saved some money last month.",
      "The lecture is about history.",
      "Sure, I'll put my bag on the chair next to me.",
      "The seats are red.",
    ], 2),
    response("m1-r4", W("Didn't you say you'd already read this novel?"), [
      "The novel is very long.",
      "I read the newspaper every day.",
      "I said hello to the author.",
      "I did, but I want to read it again before the exam.",
    ], 3),
    response("m1-r5", M("The cafeteria has run out of sandwiches again."), [
      "Let's try the café across the street.",
      "I ran there this morning.",
      "The sandwich was delicious.",
      "The cafeteria opens at noon.",
    ], 0),
    response("m1-r6", W("Why don't you ask your lab partner for help?"), [
      "The lab is on the third floor.",
      "My partner's been even busier than I have.",
      "I asked a question in class.",
      "I don't have a partner for dinner.",
    ], 1),
    response("m1-r7", M("I'm not sure whether to take the train or drive to the conference."), [
      "I trained for the marathon.",
      "The conference was interesting.",
      "The train is faster, and you can work on the way.",
      "I drove my sister to school.",
    ], 2),
    response("m1-r8", W("These instructions for the software are really confusing."), [
      "The software was expensive.",
      "I'm confused about my schedule.",
      "I installed it yesterday.",
      "Want me to show you how it works?",
    ], 3),
    {
      id: "m1-conv",
      type: "conversation",
      title: "Residence Office: A Noisy Neighbor",
      lines: [
        M("Hi, I'm in Room 312 in Fraser Hall, and I wanted to ask about switching rooms."),
        W("Okay. Can you tell me what the problem is?"),
        M("The student next door plays music really loudly, usually after midnight. I've had trouble sleeping for weeks, and it's starting to affect my grades."),
        W("I'm sorry to hear that. Have you spoken to your neighbor about it, or to your resident advisor?"),
        M("Not directly. I didn't want to cause an argument."),
        W("I understand, but a room change takes time. There's a waiting list, and it could be a month or more. In most cases, a conversation with your resident advisor present solves the problem much faster. The advisor can also remind your neighbor about quiet hours officially."),
        M("I suppose that makes sense. And if it doesn't work?"),
        W("Then come back and I'll put you on the list right away, and I'll note that you've already tried mediation, which moves you up."),
      ],
      questions: [
        {
          question: "Why does the man go to the residence office?",
          options: [
            "To report a broken window",
            "To ask about changing rooms",
            "To complain about his resident advisor",
            "To apply to become a resident advisor",
          ],
          correct: 1,
        },
        {
          question: "What does the woman suggest the man do first?",
          options: [
            "Move to another building",
            "Wear earplugs at night",
            "Talk to his neighbor with the resident advisor present",
            "Ask his professors for extensions",
          ],
          correct: 2,
        },
        {
          question: "Why does the woman mention that trying mediation \"moves you up\"?",
          options: [
            "To encourage him to try talking first without losing his place",
            "To explain the cost of a room change",
            "To show that the waiting list is short",
            "To suggest that he become a mediator",
          ],
          correct: 0,
        },
      ],
    },
    {
      id: "m1-ann",
      type: "announcement",
      title: "Graduation Ticket Pickup",
      lines: [
        W("Attention, graduating seniors. Tickets for the graduation ceremony on May 18 will be handed out at the Registrar's Office from April 28 to May 9. Each graduate will receive four guest tickets. You must collect your tickets in person and show your student ID; tickets cannot be picked up by friends or family members. If you need more than four tickets, you can add your name to a request list, and any unclaimed tickets will be shared out after May 9. Please note that the ceremony will also be streamed live on the university website."),
      ],
      questions: [
        {
          question: "What is the announcement mainly about?",
          options: [
            "How graduates can get tickets for the ceremony",
            "A change to the graduation date",
            "How to apply for graduation",
            "Rules for family members at the ceremony",
          ],
          correct: 0,
        },
        {
          question: "What can be inferred about graduates who want extra tickets?",
          options: [
            "They will receive them before April 28.",
            "They must pay for them online.",
            "They can send a family member to pick them up.",
            "They may get more if other graduates do not claim theirs.",
          ],
          correct: 3,
        },
      ],
    },
    {
      id: "m1-talk",
      type: "talk",
      title: "Marine Biology: How Salmon Find Their Way Home",
      lines: [
        M("Pacific salmon have one of the most remarkable life cycles in the animal kingdom. They hatch in freshwater streams, migrate down to the ocean, and spend several years there, sometimes traveling thousands of kilometers. Then, when they're ready to reproduce, most of them return not just to the right river, but to the very stream where they hatched. How do they do it? Researchers think two different senses are involved at different stages. In the open ocean, salmon seem to rely on the Earth's magnetic field. In experiments with young salmon in tanks, when the magnetic field around them was altered to match a different location, the fish changed direction as if to correct their route. So they appear to carry a kind of magnetic map. But once they reach the coast, the magnetic field isn't precise enough to identify a single stream. At that stage, smell takes over. Each stream has a unique chemical signature from the soil and plants around it, and young salmon memorize it before they leave. In a well-known experiment, scientists blocked the noses of some returning salmon, and those fish were far less likely to find their home stream than salmon whose sense of smell was intact."),
      ],
      questions: [
        {
          question: "What is the talk mainly about?",
          options: [
            "How salmon navigate back to their home streams",
            "Why salmon populations are declining",
            "How salmon hunt in the ocean",
            "The effects of pollution on rivers",
          ],
          correct: 0,
        },
        {
          question: "What did the experiments with young salmon in tanks suggest?",
          options: [
            "Salmon cannot sense magnetic fields.",
            "Salmon use the magnetic field to guide their direction.",
            "Salmon prefer warmer water.",
            "Salmon swim toward light.",
          ],
          correct: 1,
        },
        {
          question: "Why does the professor say the magnetic field \"isn't precise enough\" near the coast?",
          options: [
            "To argue that salmon often get lost",
            "To show that the tank experiment failed",
            "To explain why another sense becomes important",
            "To explain why salmon stay in the ocean for years",
          ],
          correct: 2,
        },
        {
          question: "What happened to the salmon whose noses were blocked?",
          options: [
            "They swam faster.",
            "They returned to the ocean.",
            "They found their home stream more easily.",
            "They were less likely to find their home stream.",
          ],
          correct: 3,
        },
      ],
    },
  ],

  // ── Harder Module 2 ──────────────────────────────────────────
  upper: [
    response("up-r1", W("I wouldn't put too much faith in that weather forecast."), [
      "So you think we should take umbrellas just in case?",
      "I put my faith in hard work.",
      "The forecast was on TV.",
      "The weather was nice yesterday.",
    ], 0),
    response("up-r2", M("Not once did the speaker mention the funding cuts."), [
      "She mentioned it once.",
      "I suppose she wanted to avoid controversy.",
      "The funding was cut last year.",
      "The speaker was very loud.",
    ], 1),
    response("up-r3", W("So much for finishing the project before the break."), [
      "The break is two weeks long.",
      "I finished my lunch already.",
      "What happened? Did something go wrong?",
      "The project is very big.",
    ], 2),
    response("up-r4", M("You'd be hard-pressed to find a better advisor than Dr. Ruiz."), [
      "I pressed the button hard.",
      "Dr. Ruiz is hard to find.",
      "I found a new advisor.",
      "That's why I asked her to supervise my thesis.",
    ], 3),
    response("up-r5", W("I'd sooner skip lunch than miss the start of that talk."), [
      "Then let's grab something quick afterward.",
      "I'd skip the talk too.",
      "Lunch starts soon.",
      "I missed the bus this morning.",
    ], 0),
    {
      id: "up-conv",
      type: "conversation",
      title: "Adjusting Research Assistant Hours",
      lines: [
        W("Professor Lindgren, thanks for seeing me. I wanted to talk about my hours as your research assistant next semester."),
        M("Of course, Mei. Is something changing?"),
        W("I've been offered a teaching assistant position in the statistics department. It's twelve hours a week, and it comes with a tuition waiver, which would make a huge difference financially."),
        M("Congratulations. That's a good opportunity. I take it you're worried about doing both?"),
        W("Well, my contract with you is fifteen hours a week. Together that's twenty-seven, plus my own courses. I don't think I can manage it without my thesis suffering."),
        M("I agree that would be too much. What if we reduced your hours to eight? The data entry for the survey project could go to one of the undergraduate interns, and you'd focus on the analysis, which is closer to your thesis anyway."),
        W("That would be ideal, actually. Would that affect my funding from your grant?"),
        M("Your pay would be reduced accordingly, but the tuition waiver should more than make up for it. Let me check with the grants office before you accept the TA position, though, just to be safe."),
      ],
      questions: [
        {
          question: "Why does the student want to change her research assistant hours?",
          options: [
            "She has been offered another position.",
            "She wants to finish her thesis early.",
            "She is unhappy with the survey project.",
            "She plans to transfer to another university.",
          ],
          correct: 0,
        },
        {
          question: "What does the professor suggest?",
          options: [
            "She should turn down the teaching position.",
            "She should work fewer hours and focus on analysis.",
            "She should take over the data entry.",
            "She should postpone her thesis.",
          ],
          correct: 1,
        },
        {
          question: "What does the professor imply when he says he will check with the grants office first?",
          options: [
            "He doubts the student's financial situation.",
            "The grants office will hire the student.",
            "He wants to increase her pay.",
            "Grant rules might affect the arrangement.",
          ],
          correct: 3,
        },
      ],
    },
    {
      id: "up-talk",
      type: "talk",
      title: "Ecology: Wolves and the Yellowstone Landscape",
      lines: [
        W("When gray wolves were reintroduced to Yellowstone National Park in 1995, after an absence of about seventy years, researchers expected the wolves to reduce the number of elk. What they didn't fully anticipate was how far the effects might spread. Without wolves, elk had been feeding heavily on young willow and aspen trees along rivers, preventing them from growing. After the wolves returned, studies reported that some of these trees began to recover. The popular explanation was something called the 'ecology of fear': elk, now at risk of being hunted, avoided open riverbanks where they could easily be trapped, which gave young trees a chance. Recovering trees, in turn, were said to stabilize riverbanks and provide food for beavers, whose dams created ponds for fish and birds. This chain of effects, starting at the top of the food web and moving down, is called a trophic cascade, and Yellowstone became its most famous example. However, I want to add a note of caution. More recent research suggests the story is more complicated. Elk numbers were also reduced by bears, by human hunting outside the park and by severe winters, and in some areas the trees have hardly recovered at all, partly because, without beavers for decades, the streams had cut deeper channels and the water table dropped. So wolves certainly mattered, but the idea that they single-handedly restored the ecosystem is probably an oversimplification."),
      ],
      questions: [
        {
          question: "What is the talk mainly about?",
          options: [
            "The effects of bringing wolves back to Yellowstone and debates about them",
            "How beavers build dams",
            "Why elk populations are growing",
            "The history of hunting in national parks",
          ],
          correct: 0,
        },
        {
          question: "What is the \"ecology of fear\"?",
          options: [
            "Wolves avoiding areas with bears",
            "Elk changing where they feed to avoid wolves",
            "Visitors staying away from the park",
            "Beavers abandoning rivers",
          ],
          correct: 1,
        },
        {
          question: "Why does the professor mention bears, hunting and severe winters?",
          options: [
            "To explain why wolves were originally removed",
            "To argue that wolves had no effect at all",
            "To show that other factors also reduced elk numbers",
            "To describe dangers to park visitors",
          ],
          correct: 2,
        },
        {
          question: "What is the professor's attitude toward the popular story about Yellowstone?",
          options: [
            "She thinks it is completely false.",
            "She thinks it is accurate in every detail.",
            "She believes it has no scientific basis.",
            "She thinks it contains some truth but is oversimplified.",
          ],
          correct: 3,
        },
      ],
    },
  ],

  // ── Easier Module 2 ──────────────────────────────────────────
  lower: [
    response("low-r1", W("How are you getting to the airport?"), [
      "I'm taking a taxi.",
      "The airport is big.",
      "My flight was late.",
      "I like airplanes.",
    ], 0),
    response("low-r2", M("Is it going to rain today?"), [
      "I like the rain.",
      "The radio said it might this afternoon.",
      "It rained a lot.",
      "My umbrella is red.",
    ], 1),
    response("low-r3", W("What's your favorite subject?"), [
      "I have five subjects.",
      "School starts at eight.",
      "I really enjoy biology.",
      "My teacher is nice.",
    ], 2),
    response("low-r4", M("Could you open the window, please?"), [
      "I opened a bank account.",
      "The window is big.",
      "It's a nice view.",
      "Sure, it is a bit warm in here.",
    ], 3),
    response("low-r5", W("Did you enjoy the concert?"), [
      "Yes, the band was fantastic.",
      "It starts at eight.",
      "I bought a ticket.",
      "The concert hall is downtown.",
    ], 0),
    {
      id: "low-ann",
      type: "announcement",
      title: "Library Quiet Floor",
      lines: [
        W("Good afternoon. Please remember that the third floor of the library is a quiet floor. Phone calls and group discussions are not allowed there. If you want to study with friends, please use the group study rooms on the second floor. You can book these rooms at the front desk. Thank you for helping us keep the library a good place to study."),
      ],
      questions: [
        {
          question: "What is not allowed on the third floor?",
          options: ["Reading", "Using laptops", "Phone calls", "Studying alone"],
          correct: 2,
        },
        {
          question: "Where can students study in groups?",
          options: [
            "On the third floor",
            "In the study rooms on the second floor",
            "At the front desk",
            "In the cafeteria",
          ],
          correct: 1,
        },
      ],
    },
    {
      id: "low-talk",
      type: "talk",
      title: "Weather: What Causes Thunder",
      lines: [
        M("Have you ever wondered why thunder follows lightning? Lightning is a giant spark of electricity in the sky. It is extremely hot, about five times hotter than the surface of the sun. When lightning flashes, it heats the air around it very quickly. The hot air expands so fast that it makes a loud boom. That boom is thunder. We see lightning before we hear thunder because light travels much faster than sound. You can even count the seconds between the flash and the thunder to find out how far away a storm is. About three seconds equals one kilometer."),
      ],
      questions: [
        {
          question: "What causes thunder?",
          options: [
            "Clouds hitting each other",
            "Air expanding quickly after being heated by lightning",
            "Rain falling on the ground",
            "Wind blowing very fast",
          ],
          correct: 1,
        },
        {
          question: "Why do we see lightning before we hear thunder?",
          options: [
            "Lightning happens first.",
            "Thunder is very quiet.",
            "Light travels faster than sound.",
            "Our eyes are faster than our ears.",
          ],
          correct: 2,
        },
        {
          question: "If you count six seconds between the lightning and the thunder, about how far away is the storm?",
          options: ["Two kilometers", "Six kilometers", "Three kilometers", "One kilometer"],
          correct: 0,
        },
      ],
    },
  ],
};
